import { createStart, createMiddleware } from "@tanstack/react-start";

import { renderErrorPage } from "./lib/error-page";
import { attachSupabaseAuth } from "@/integrations/supabase/auth-attacher";
import { CACHE_PRESETS, isCachablePublicPagePath } from "@/lib/cache/cacheHeaders";
import { outcomeFromCfStatus, recordCacheEvent } from "@/lib/cache/cacheMetrics";

const errorMiddleware = createMiddleware().server(async ({ next }) => {
  try {
    return await next();
  } catch (error) {
    if (error != null && typeof error === "object" && "statusCode" in error) {
      throw error;
    }
    console.error(error);
    return new Response(renderErrorPage(), {
      status: 500,
      headers: { "content-type": "text/html; charset=utf-8" },
    });
  }
});

/**
 * PR-35B — Public page edge cache middleware.
 * Stamps Cache-Control on HTML responses for hot public landing
 * routes (/, /welcome, /r/*, /s/*, /wc/*) without touching any
 * route owned by an authenticated user or any API endpoint.
 * Falls open: any error here returns the original response.
 */
const publicPageCacheMiddleware = createMiddleware().server(async ({ next, request }) => {
  const result = await next();
  try {
    const res = result.response;
    if (!(res instanceof Response)) return result;
    if (res.status < 200 || res.status >= 400) return result;
    const url = new URL(request.url);
    if (!isCachablePublicPagePath(url.pathname)) return result;
    const ct = res.headers.get("content-type") ?? "";
    if (!ct.includes("text/html")) return result;
    const label = publicLabel(url.pathname);
    if (res.headers.has("set-cookie")) {
      recordCacheEvent(`page:${label}`, "bypass");
      return result;
    }
    if (res.headers.get("cache-control")) return result;
    res.headers.set("cache-control", CACHE_PRESETS.publicPage);
    res.headers.set("vary", joinVary(res.headers.get("vary"), "accept-encoding, cookie"));
    recordCacheEvent(`page:${label}`, outcomeFromCfStatus(request.headers.get("cf-cache-status")));
    return result;
  } catch {
    return result;
  }
});

function joinVary(existing: string | null, extra: string): string {
  if (!existing) return extra;
  const set = new Set(existing.split(",").map((s) => s.trim().toLowerCase()).filter(Boolean));
  for (const v of extra.split(",")) set.add(v.trim().toLowerCase());
  return Array.from(set).join(", ");
}

function publicLabel(pathname: string): string {
  if (pathname === "/") return "/";
  if (pathname === "/welcome") return "/welcome";
  if (pathname.startsWith("/r/")) return "/r/*";
  if (pathname.startsWith("/s/")) return "/s/*";
  if (pathname.startsWith("/wc/")) return "/wc/*";
  return pathname;
}

export const startInstance = createStart(() => ({
  requestMiddleware: [errorMiddleware, publicPageCacheMiddleware],
  functionMiddleware: [attachSupabaseAuth],
}));
